import { useMemo, useState } from "react";
import {
  ChevronRight,
  ChevronsUpDown,
  Globe,
  LayoutList,
  Link2,
  Loader2,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDropdownTree } from "../hooks/useDropdowns";
import type { CategoryMeta, ModuleNode, PageNode } from "../types";

interface CategoryTreeProps {
  selectedCategory: string | null;
  onSelect: (meta: CategoryMeta) => void;
}

const SYSTEM_MODULE = "System";

const MODULE_ORDER = [
  "System",
  "Daily Work",
  "Planning",
  "Business Structure",
  "Finance",
  "Administration",
];

function moduleRank(name: string): number {
  const idx = MODULE_ORDER.indexOf(name);
  return idx === -1 ? MODULE_ORDER.length : idx;
}

function moduleKey(module: string): string {
  return `m:${module}`;
}

function pageKey(module: string, page: string): string {
  return `p:${module}::${page}`;
}

export function CategoryTree({
  selectedCategory,
  onSelect,
}: CategoryTreeProps) {
  const { data: treeRaw = [], isLoading, isError } = useDropdownTree();

  const tree = useMemo<ModuleNode[]>(() => {
    return [...treeRaw].sort(
      (a, b) => moduleRank(a.module) - moduleRank(b.module),
    );
  }, [treeRaw]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState("");

  const term = search.trim().toLowerCase();
  const isSearching = term.length > 0;

  function dropdownMatches(d: CategoryMeta): boolean {
    if (!isSearching) return true;
    return (
      d.name.toLowerCase().includes(term) ||
      d.category.toLowerCase().includes(term)
    );
  }

  // While searching, derive expansion automatically so all matches are visible.
  const effectiveExpanded = useMemo(() => {
    if (!isSearching) return expanded;
    const exp = new Set<string>();
    for (const m of tree) {
      let moduleHasMatch = false;
      for (const p of m.pages) {
        if (p.dropdowns.some(dropdownMatches)) {
          exp.add(pageKey(m.module, p.page));
          moduleHasMatch = true;
        }
      }
      if (moduleHasMatch) exp.add(moduleKey(m.module));
    }
    return exp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, expanded, isSearching, term]);

  function toggleKey(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function expandAllNow() {
    const all = new Set<string>();
    for (const m of tree) {
      all.add(moduleKey(m.module));
      for (const p of m.pages) all.add(pageKey(m.module, p.page));
    }
    setExpanded(all);
  }

  function collapseAllNow() {
    setExpanded(new Set());
  }

  function toggleAll() {
    const totalKeys = tree.reduce(
      (acc, m) => acc + 1 + m.pages.length,
      0,
    );
    if (expanded.size >= totalKeys) collapseAllNow();
    else expandAllNow();
  }

  function pageVisible(p: PageNode): boolean {
    if (!isSearching) return true;
    return p.dropdowns.some(dropdownMatches);
  }

  function moduleVisible(m: ModuleNode): boolean {
    if (!isSearching) return true;
    return m.pages.some(pageVisible);
  }

  return (
    <div
      className="sticky top-0 flex max-h-[calc(100vh-8rem)] w-[280px] shrink-0 flex-col overflow-hidden rounded-xl border bg-card"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div
        className="flex items-center gap-2 border-b px-3 py-2.5"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dropdowns..."
            className="h-8 pl-7 text-[12px]"
          />
        </div>
        <button
          type="button"
          onClick={toggleAll}
          title="Expand all / Collapse all"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
        >
          <ChevronsUpDown className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            Loading…
          </div>
        ) : isError ? (
          <div className="px-4 py-2 text-xs text-destructive">
            Failed to load tree.
          </div>
        ) : tree.length === 0 ? (
          <div className="px-4 py-2 text-xs text-muted-foreground">
            No dropdowns yet.
          </div>
        ) : (
          tree.filter(moduleVisible).map((mod) => {
            const mKey = moduleKey(mod.module);
            const mOpen = effectiveExpanded.has(mKey);
            const isSystemModule = mod.module === SYSTEM_MODULE;
            return (
              <div key={mod.module} className="mt-4 first:mt-0">
                <button
                  type="button"
                  onClick={() => toggleKey(mKey)}
                  className="w-full text-left text-[10px] font-semibold uppercase transition-colors hover:text-foreground"
                  style={{
                    paddingLeft: 8,
                    paddingRight: 12,
                    letterSpacing: "0.8px",
                    color: "hsl(215,16%,47%)",
                  }}
                >
                  {mod.module}
                </button>
                {mOpen && (
                  <div className="mt-1">
                    {mod.pages.filter(pageVisible).map((p) => {
                      const pKey = pageKey(mod.module, p.page);
                      const pOpen = effectiveExpanded.has(pKey);
                      return (
                        <div key={pKey}>
                          <button
                            type="button"
                            onClick={() => toggleKey(pKey)}
                            className="flex w-full items-center gap-1 py-1.5 text-[13px] text-[hsl(215,16%,47%)] transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
                            style={{ paddingLeft: 16, paddingRight: 12 }}
                          >
                            <ChevronRight
                              className="h-3.5 w-3.5 transition-transform"
                              strokeWidth={1.5}
                              style={{
                                transform: pOpen
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                              }}
                            />
                            <span className="font-medium">{p.page}</span>
                          </button>
                          {pOpen && (
                            <ul>
                              {p.dropdowns
                                .filter(dropdownMatches)
                                .map((d) => {
                                  const active = d.category === selectedCategory;
                                  const Icon = isSystemModule
                                    ? Globe
                                    : LayoutList;
                                  return (
                                    <li key={d.category}>
                                      <button
                                        type="button"
                                        onClick={() => onSelect(d)}
                                        className={cn(
                                          "mx-1.5 flex w-[calc(100%-12px)] items-center justify-between gap-2 rounded-md py-1.5 text-left text-[13px] text-foreground transition-colors hover:bg-[hsl(210,50%,96%)]",
                                          active &&
                                            "bg-[hsl(210,55%,92%)] font-medium text-[hsl(210,100%,28%)] hover:bg-[hsl(210,55%,92%)]",
                                        )}
                                        style={{
                                          paddingLeft: 28 - 6,
                                          paddingRight: 8,
                                        }}
                                      >
                                        <span className="flex min-w-0 items-center gap-2">
                                          <Icon
                                            className="h-3 w-3 shrink-0"
                                            strokeWidth={1.5}
                                          />
                                          <span className="truncate">
                                            {d.name}
                                          </span>
                                          {d.parentCategory && (
                                            <span
                                              title={`Depends on: ${d.parentCategory}`}
                                              className="shrink-0"
                                            >
                                              <Link2
                                                className="h-3 w-3 text-muted-foreground"
                                                strokeWidth={1.5}
                                                aria-label={`Depends on ${d.parentCategory}`}
                                              />
                                            </span>
                                          )}
                                        </span>
                                        <span
                                          className={cn(
                                            "ml-2 inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                                            active
                                              ? "bg-white text-[hsl(210,100%,28%)]"
                                              : "bg-[hsl(210,30%,93%)] text-[hsl(215,16%,40%)]",
                                          )}
                                        >
                                          {d.itemCount}
                                        </span>
                                      </button>
                                    </li>
                                  );
                                })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
